import React from "react";
import { Box, Select, MenuItem, SelectChangeEvent } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import {
  PersonOutlined,
  NotificationsOutlined,
  PeopleOutlined,
  BusinessOutlined,
  PlaceOutlined,
  PaymentsOutlined,
  BuildOutlined,
  LocalShippingOutlined,
  PaletteOutlined,
} from "@mui/icons-material";
import useTranslation from "../../../shared/hooks/useTranslation";
import { SettingsNavSection } from "../hooks/useSettingsNav";
import { dt } from "../../../shared/themes/redesignTokens";

const iconMap: Record<string, React.ReactNode> = {
  PersonOutlined: <PersonOutlined sx={{ fontSize: "18px", color: dt.blue }} />,
  NotificationsOutlined: <NotificationsOutlined sx={{ fontSize: "18px", color: dt.blue }} />,
  PeopleOutlined: <PeopleOutlined sx={{ fontSize: "18px", color: dt.blue }} />,
  BusinessOutlined: <BusinessOutlined sx={{ fontSize: "18px", color: dt.blue }} />,
  PlaceOutlined: <PlaceOutlined sx={{ fontSize: "18px", color: dt.blue }} />,
  PaymentsOutlined: <PaymentsOutlined sx={{ fontSize: "18px", color: dt.blue }} />,
  BuildOutlined: <BuildOutlined sx={{ fontSize: "18px", color: dt.blue }} />,
  LocalShippingOutlined: <LocalShippingOutlined sx={{ fontSize: "18px", color: dt.blue }} />,
  PaletteOutlined: <PaletteOutlined sx={{ fontSize: "18px", color: dt.blue }} />,
};

interface SettingsSectionSelectorProps {
  sections: SettingsNavSection[];
}

const SettingsSectionSelector: React.FC<SettingsSectionSelectorProps> = ({ sections }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { translate } = useTranslation("settings");

  const currentSection =
    sections.find(
      (s) =>
        location.pathname === `/settings/${s.path}` ||
        location.pathname.startsWith(`/settings/${s.path}/`)
    )?.path || "profile";

  const handleChange = (event: SelectChangeEvent<string>) => {
    navigate(`/settings/${event.target.value}`);
  };

  return (
    <Select
      value={currentSection}
      onChange={handleChange}
      fullWidth
      size="small"
      renderValue={(value) => {
        const section = sections.find((s) => s.path === value);
        if (!section) return null;
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {iconMap[section.iconName]}
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "14px",
                fontWeight: 600,
                color: dt.textPrimary,
              }}
            >
              {translate(section.labelKey)}
            </Box>
          </Box>
        );
      }}
      sx={{
        backgroundColor: dt.cardBg,
        borderRadius: dt.radiusSm,
        fontFamily: dt.font,
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: dt.cardBorder,
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: dt.blue,
          boxShadow: `0 0 0 3px ${dt.blueGlow}`,
        },
      }}
      MenuProps={{
        PaperProps: {
          sx: {
            borderRadius: "12px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.04)",
            border: `1px solid ${dt.cardBorder}`,
            mt: 0.5,
          },
        },
      }}
    >
      {sections.map((section) => (
        <MenuItem key={section.key} value={section.path}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {iconMap[section.iconName]}
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "13px",
                fontWeight: section.path === currentSection ? 600 : 400,
                color: section.path === currentSection ? dt.blue : dt.textPrimary,
              }}
            >
              {translate(section.labelKey)}
            </Box>
          </Box>
        </MenuItem>
      ))}
    </Select>
  );
};

export default SettingsSectionSelector;
